import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { Logger } from '@sondermind/datadog-logging';
import { NotificationToastService, ToastType } from '@sondermind/features/notification-toast';
import { EngagementService } from '@sondermind/data-access/engagement';
import { TASK_CODES } from '@sondermind/constants';

import { OnboardingStepShellComponent } from '../onboarding-step-shell/onboarding-step-shell.component';
import { CaqhStepperComponent } from '../caqh-flow-page/ui/caqh-stepper/caqh-stepper.component';
import { IW9Interview, W9InterviewHttpService } from './services/w9-interview.httpservice';
import { SIG_TIP, W9PdfService } from './services/w9-pdf.service';
import { W9AgentPanelComponent } from './agent-panel/w9-agent-panel/w9-agent-panel.component';
import { W9TinType } from './models/w9-tin-type';

@Component({
  selector: 'member-w9-review-page',
  templateUrl: './w9-review-page.component.html',
  styleUrls: ['./w9-review-page.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, W9AgentPanelComponent, OnboardingStepShellComponent, CaqhStepperComponent],
})
export class W9ReviewPageComponent implements OnInit, AfterViewInit {
  @ViewChild('pdfCanvas') pdfCanvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('pdfContainer') pdfContainerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('pdfAnnotationLayer') pdfAnnotationLayerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('w9HintPanel') hintPanelRef?: ElementRef<HTMLDivElement>;

  private router = inject(Router);
  private interviewService = inject(W9InterviewHttpService);
  private pdfService = inject(W9PdfService);
  private appService = inject(SondermindApplicationService);
  private logger = inject(Logger);
  private toastService = inject(NotificationToastService);
  private engagementService = inject(EngagementService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  readonly providerId = this.appService.currentUser.id?.toString() ?? '';
  readonly providerFirstName = this.appService.currentUser.firstName ?? '';
  readonly SIG_TIP = SIG_TIP;

  readonly isLoading = signal(true);
  readonly loadError = signal<'no-interview' | 'generic' | null>(null);
  readonly isSubmitting = signal(false);
  readonly tinValue = signal('');
  readonly signatureValue = signal('');
  readonly resolvedTinType = signal<W9TinType>('ssn');
  readonly interview = signal<IW9Interview | null>(null);
  readonly today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

  // The typed signature and its date are the legal sign-off ceremony shown on the
  // rendered W-9. They gate submission and are sent to the backend on confirm
  // alongside the TIN (which is entered into the live PDF fields).
  readonly canSubmit = computed(
    () => this.tinValue().length === 9 && this.signatureValue().length > 0 && !this.isSubmitting()
  );

  readonly readyToRender = signal<{ bytes: Uint8Array; tinType: W9TinType; } | null>(null);
  private hasRendered = false;
  private isRefetching = false;
  private tinScrolled = false;
  private sigScrolled = false;

  constructor() {
    effect(() => {
      const pending = this.readyToRender();
      if (!pending || this.hasRendered) return;
      // Claim the single render slot synchronously so the effect can only ever
      // schedule one microtask, even if readyToRender were set more than once.
      this.hasRendered = true;
      // The @if reveals the canvas in the same CD pass that flips isLoading() to
      // false; defer one microtask so the ViewChild refs resolve before we render.
      queueMicrotask(() => {
        const canvas = this.pdfCanvasRef?.nativeElement;
        const annotationLayer = this.pdfAnnotationLayerRef?.nativeElement;
        const container = this.pdfContainerRef?.nativeElement;
        if (!canvas || !annotationLayer || !container) return;
        this.pdfService
          .renderInteractivePdf(
            { canvas, annotationLayer, container },
            pending.bytes,
            pending.tinType,
            (tin) => this.tinValue.set(tin),
            this.hintPanelRef?.nativeElement
          )
          .then(() => {
            if (this.tinScrolled) return;
            const tinEl = annotationLayer.querySelector('input') as HTMLElement | null;
            if (tinEl) {
              this.tinScrolled = true;
              tinEl.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
            }
          })
          .catch((err) => this.logger.error('W9ReviewPageComponent: render failed', { err }));
      });
    });

    effect(() => {
      // Once the TIN reaches 9 digits, scroll to the signature field once.
      if (this.tinValue().length !== 9 || this.sigScrolled) return;
      this.sigScrolled = true;
      this.scrollToSignature();
    });
  }

  ngOnInit(): void {
    this.loadData().catch((err) => {
      this.logger.error('W9ReviewPageComponent: unhandled load error', { err });
    });
  }

  ngAfterViewInit(): void {
    this.appService.pageLoaded = true;
  }

  private async loadData(): Promise<void> {
    try {
      const [interviewResult, pdfBytes] = await Promise.all([
        this.fetchInterview(),
        this.pdfService.fetchPdfBytes(),
      ]);

      if (!interviewResult) {
        this.loadError.set('no-interview');
        this.isLoading.set(false);
        this.cdr.markForCheck();
        return;
      }

      this.interview.set(interviewResult);
      const tinType = interviewResult.tinType ?? this.pdfService.deriveTinType(interviewResult);
      this.resolvedTinType.set(tinType);
      const filledBytes = await this.pdfService.fillAndFlatten(pdfBytes, interviewResult, tinType);
      this.isLoading.set(false);
      this.readyToRender.set({ bytes: filledBytes, tinType });
    } catch (err) {
      this.logger.error('W9ReviewPageComponent: failed to load', { err });
      this.loadError.set('generic');
      this.isLoading.set(false);
      this.cdr.markForCheck();
    }
  }

  private fetchInterview(): Promise<IW9Interview | null> {
    return new Promise((resolve, reject) => {
      this.interviewService
        .getLatestInterview()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({ next: resolve, error: reject });
    });
  }

  onSubmit(): void {
    const currentInterview = this.interview();
    const tin = this.tinValue();
    if (!currentInterview || !tin) return;

    this.isSubmitting.set(true);

    this.interviewService
      .confirmInterview(currentInterview.id, {
        tin,
        tinType: this.resolvedTinType(),
        certification: {
          signature: this.signatureValue(),
          date: this.today,
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.markW9TaskCompleted();
          this.toastService.showNotificationToast({
            message: 'Your W-9 has been saved',
            type: ToastType.SUCCESS,
            autoClose: true,
          });
          this.router.navigateByUrl('/dashboard');
        },
        error: (err) => {
          this.logger.error('W9ReviewPageComponent: confirm failed', { err });
          this.toastService.showNotificationToast({
            message: 'Submission failed. Please check your TIN and try again.',
            type: ToastType.ERROR,
            autoClose: true,
          });
          this.isSubmitting.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  showHint(inputEl: HTMLElement, tipHtml: string): void {
    const panel = this.hintPanelRef?.nativeElement;
    if (!panel) return;
    panel.innerHTML = tipHtml;
    const containerRect = this.pdfContainerRef?.nativeElement.getBoundingClientRect();
    const inputRect = inputEl.getBoundingClientRect();
    const left = (containerRect?.right ?? inputRect.right) + 14;
    const top = Math.min(Math.max(inputRect.top - 6, 10), window.innerHeight - 160);
    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
    panel.classList.add('w9-review-page__hint-panel--visible');
  }

  hideHint(): void {
    this.hintPanelRef?.nativeElement?.classList.remove('w9-review-page__hint-panel--visible');
  }

  onFieldsUpdated(): void {
    if (this.isRefetching) return;
    this.isRefetching = true;
    // Clear the entire form: stale TIN/signature must not survive an agent-driven
    // refetch, and resetting the render guard rebuilds the PDF cleanly.
    this.tinValue.set('');
    this.signatureValue.set('');
    this.hasRendered = false;
    this.tinScrolled = false;
    this.sigScrolled = false;
    this.loadData()
      .catch((err) => {
        this.logger.error('W9ReviewPageComponent: refetch after field update failed', { err });
      })
      .finally(() => {
        this.isRefetching = false;
      });
  }

  onCrumbNavigate(subStep: number): void {
    const behavior = this.scrollBehavior();
    if (subStep === 0) {
      this.pdfContainerRef?.nativeElement?.scrollIntoView({ behavior, block: 'start' });
      return;
    }
    if (subStep === 2) {
      this.pdfContainerRef?.nativeElement
        ?.querySelector('.w9-review-page__sig-input')
        ?.scrollIntoView({ behavior, block: 'center' });
      return;
    }
    // subStep 1: TIN — the input lives inside the pdf.js annotation layer
    const layer = this.pdfAnnotationLayerRef?.nativeElement;
    const tinEl = layer?.querySelector('input') as HTMLElement | null;
    tinEl?.scrollIntoView({ behavior, block: 'center' });
  }

  private scrollBehavior(): ScrollBehavior {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  private scrollToSignature(): void {
    const sig = this.pdfContainerRef?.nativeElement?.querySelector('.w9-review-page__sig-input') as HTMLElement | null;
    queueMicrotask(() => sig?.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' }));
  }

  private markW9TaskCompleted(): void {
    // Fire-and-forget: the W-9 is already confirmed in service-engagement, so the
    // onboarding-task flag is secondary and must not block navigation. Log on failure.
    //
    // Deliberately NOT tied to takeUntilDestroyed: this fires immediately before
    // navigateByUrl('/account') in the confirm success handler, which destroys this
    // component in the same tick. takeUntilDestroyed would unsubscribe on destroy and
    // cancel the in-flight request (browser net::ERR_ABORTED), so the task would never
    // be marked done. The call self-completes and handles its own errors, so letting it
    // outlive the component is safe.
    this.engagementService
      .markTaskCompleted$(TASK_CODES.W9)
      .subscribe({
        error: (err: unknown) => this.logger.error('W9ReviewPageComponent: failed to mark W-9 task completed', { err }),
      });
  }
}
